import React, { useState, useEffect } from 'react'
import Button from '../../components/Button/Button'
import Text from '../../components/Text/Text'
import LinkExternal from '../../components/Link/LinkExternal'
import Row, { RowBetween } from 'components/Row'
import Flex from '../../components/Box/Flex'
import { Modal } from '../Modal'
import { connectorLocalStorageKey } from './config'
import { isMobile } from 'react-device-detect'
import { isTransactionRecent, useAllTransactions } from 'state/transactions/hooks'
import { TransactionDetails } from 'state/transactions/reducer'
import { getKCCExplorerLink } from 'utils'
import Loader from 'components/Loader'
import { Box, CheckmarkCircleIcon, ErrorIcon } from 'uikit'
import { useActiveWeb3React } from 'hooks'
import styled, { useTheme } from 'styled-components'
import { useDispatch } from 'react-redux'
import { clearAllTransactions } from 'state/transactions/actions'
import AccountTab from './AccountTab'
import copy from 'copy-to-clipboard'
import useToast from 'hooks/useToast'
import { useGetMjtBalance } from 'hooks/useTokenBalance'
import BN from 'bignumber.js'

import { useGetKcsBalance } from 'hooks/useTokenBalance'

import Tooltip from 'components/Tooltip'
import { useTranslation } from 'react-i18next'

interface Props {
  account: string
  logout: () => void
  onDismiss?: () => void
}

const ClearAll = styled(Text)`
  cursor: pointer;
  &:hover {
    color: #18bb97;
  }
`

const CellContainer = styled.div`
  background: #00142a0a;
  width: 100%;
  border-radius: 8px;
  padding: 15px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
`

const Lines = styled.div`
  background: rgba(115, 126, 141, 0.16);
  height: 1px;
  width: 100%;
  margin: 10px 0;
`

const WalletContainer = styled.div`
  background: #00142a0a;
  width: 100%;
  border-radius: 8px;
  padding: 15px;
  margin-top: 12px;
  margin-bottom: 20px;
`

const ImgCopy = styled.img`
  width: 18px;
  height: 18px;
  cursor: pointer;
`

const newTransactionsFirst = (a: TransactionDetails, b: TransactionDetails) => b.addedTime - a.addedTime

const getRowStatus = (sortedRecentTransaction: TransactionDetails, chainId?: any) => {
  const { hash, receipt } = sortedRecentTransaction

  // now - addTime > 15s && !confirmTime

  if (!receipt) {
    return {
      icon: (
        <Box
          style={{ cursor: 'pointer' }}
          onClick={() => {
            window.open(
              'https://docs.mojitoswap.finance/q-and-a/how-to-unstuck-stuck-transactions-on-metamask',
              '_blank'
            )
          }}
        >
          <Loader stroke="#001425" />
        </Box>
      ),
      color: 'text',
      url: 'https://docs.mojitoswap.finance/q-and-a/how-to-unstuck-stuck-transactions-on-metamask',
    }
  }

  if (hash && receipt?.status === 1) {
    return {
      icon: <CheckmarkCircleIcon color="success" />,
      color: 'success',
      url: getKCCExplorerLink(chainId, hash, 'transaction'),
    }
  }

  return {
    icon: <ErrorIcon color="#FF8F2C" />,
    color: '#FF8F2C',
    url: getKCCExplorerLink(chainId, hash, 'transaction'),
  }
}

const AccountModal: React.FC<Props> = ({ account, logout, onDismiss = () => null }) => {
  const { chainId, account: activeAccount } = useActiveWeb3React()
  const allTransactions = useAllTransactions()
  const { balance: mjtBalance } = useGetMjtBalance()
  const { balance: kcsBalance } = useGetKcsBalance()
  // const { nfts, userNftsInitializationState } = useUserNfts()
  const [tab, setTab] = useState(0)
  const theme = useTheme()
  const { toastInfo } = useToast()
  const [show, setShow] = useState<boolean>(false)
  const { t } = useTranslation()

  const sortedRecentTransactions = React.useMemo(() => {
    const maxLength = 20
    const txs = Object.values(allTransactions) as any
    const list = txs.filter(isTransactionRecent).sort(newTransactionsFirst)
    if (list.length > maxLength) {
      list.splice(maxLength, list.length - maxLength)
    }
    return list
  }, [allTransactions])

  const dispatch = useDispatch()

  const clearAllTransaction = () => {
    dispatch(clearAllTransactions({ chainId: chainId ?? 321 }))
  }

  const clickListener = (e) => {
    const target = window.event || e
    const parentApp = document.getElementById('accountTip')
    if (parentApp && !parentApp.contains(target?.target as any)) {
      setShow(false)
    }
  }
  useEffect(() => {
    window.addEventListener('mousedown', clickListener)
    return () => window.removeEventListener('mousedown', clickListener)
  })

  const Tips = (
    <>
      <Text color={theme.colors.textRemark} fontSize="14px">
        {t('UIKIT_15')}
      </Text>
      <LinkExternal
        size="12px"
        fontSize="14px"
        style={{ fontSize: '14px', fontWeight: 500 }}
        small
        href="/swap"
        target="_self"
        mr="16px"
      >
        <span style={{ color: '#2BCAA0', fontSize: '14px' }}>{t('UIKIT_5')}</span>
      </LinkExternal>
    </>
  )

  const renderTabWallet = () => {
    return (
      <>
        <Text bold fontSize="14px">
          {t('UIKIT_6')}
        </Text>
        <CellContainer>
          <Text color={theme.colors.textRemark} fontSize="14px" maxWidth="300px" style={{ wordBreak: 'break-all' }}>
            {activeAccount}
          </Text>
          <ImgCopy
            src={require('assets/images/menu/copy.svg').default}
            onClick={() => {
              copy(activeAccount)
              toastInfo(t('UIKIT_14'))
            }}
          />
        </CellContainer>
        <Text bold fontSize="14px" mt="24px">
          {t('UIKIT_7')}
        </Text>
        <WalletContainer>
          <RowBetween>
            <Text color={theme.colors.textRemark} fontSize="14px">
              KCS
            </Text>
            <Row style={{ width: 'auto' }}>
              <Text color={theme.colors.textRemark} fontSize="14px">
                {new BN(kcsBalance.toString()).div(10 ** 18).toFixed(2, 1)}
              </Text>
              {new BN(kcsBalance.toString()).div(10 ** 18).lt(2) && (
                <Tooltip text={Tips} show={show} placement="top">
                  <ImgCopy
                    src={require('assets/images/menu/warning.svg').default}
                    style={{ marginLeft: '4px' }}
                    onClick={() => {
                      setShow(!show)
                    }}
                    id="accountTip"
                  />
                </Tooltip>
              )}
            </Row>
          </RowBetween>
          <Lines />
          <RowBetween>
            <Text color={theme.colors.textRemark} fontSize="14px">
              MJT
            </Text>
            <Text color={theme.colors.textRemark} fontSize="14px">
              {new BN(mjtBalance.toString()).div(10 ** 18).toFixed(2, 1)}
            </Text>
          </RowBetween>
          <Lines />
          {/* <RowBetween>
            <Text color={theme.colors.textRemark} fontSize="14px">
              NFT
            </Text>
            <Text color={theme.colors.textRemark} fontSize="14px">
              {userNftsInitializationState === UserNftInitializationState.INITIALIZED ? nfts.length : '-'}
            </Text>
          </RowBetween> */}
        </WalletContainer>
        <LinkExternal
          style={{ fontSize: '16px', fontWeight: 500 }}
          small
          href={`https://explorer.kcc.io/address/${account}`}
          mr="16px"
        >
          <span style={{ color: '#2BCAA0' }}>{t('UIKIT_8')}</span>
        </LinkExternal>
        <Flex justifyContent="center">
          <Button
            scale="md"
            style={{
              width: '100%',
              margin: '30px 0 10px 0',
              color: '#FF5B65',
              background: 'white',
              border: '2px solid #FF5B65',
            }}
            onClick={() => {
              logout()
              window.localStorage.removeItem(connectorLocalStorageKey)
              onDismiss()
            }}
          >
            {t('UIKIT_9')}
          </Button>
        </Flex>
      </>
    )
  }

  const renderTransaction = () => {
    return (
      <>
        {account && chainId && sortedRecentTransactions.length === 0 && (
          <>
            <Text mb="8px" bold>
              {t('UIKIT_10')}
            </Text>
            <Text color={theme.colors.borderDark} mt="40px" mb="100px" textAlign="center">
              {t('UIKIT_11')}
            </Text>
          </>
        )}

        {account && chainId && sortedRecentTransactions.length > 0 && (
          <Flex justifyContent="space-between" style={{ width: '100%' }} flexDirection="row" alignItems="center">
            <Text mb="8px" bold textAlign="left">
              {t('UIKIT_10')}
            </Text>

            <ClearAll mb="8px" bold textAlign="left" onClick={clearAllTransaction}>
              {t('UIKIT_12')}
            </ClearAll>
          </Flex>
        )}
        {account &&
          chainId &&
          sortedRecentTransactions.map((sortedRecentTransaction, index) => {
            const { hash, summary } = sortedRecentTransaction
            const { icon, color, url } = getRowStatus(sortedRecentTransaction, chainId)

            return (
              <>
                <Flex key={`${hash}${index + 1}`} alignItems="center" justifyContent="space-between" mb="4px">
                  <LinkExternal style={{ fontSize: '14px' }} href={url} color={color}>
                    {summary ?? hash}
                  </LinkExternal>
                  {icon}
                </Flex>
              </>
            )
          })}
      </>
    )
  }

  return (
    <Modal title={t('UIKIT_13')} minWidth={isMobile ? '96%' : '440px'} onDismiss={onDismiss}>
      <Flex justifyContent="center">
        <AccountTab
          tab={tab}
          clickTab={(index) => {
            setTab(index)
          }}
        />
      </Flex>
      {tab === 0 ? renderTabWallet() : renderTransaction()}
    </Modal>
  )
}

export default AccountModal
